import {
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';

import { XwaiV2StreamService } from '../stream/xwai-v2-stream.service';

/**
 * The chat composer: textarea + send button, with shell-style up/down history
 * recall and auto-growing height. Owns only the local input draft; the
 * conversation itself lives in the root-scoped {@link XwaiV2StreamService}, so
 * this can be dropped anywhere in the shell and drive the same chat.
 */
@Component({
  selector: 'app-xwai-input',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzButtonModule, NzIconModule],
  templateUrl: './xwai-input.component.html',
  styleUrls: ['./xwai-input.component.less'],
})
export class XwaiInputComponent {
  private service = inject(XwaiV2StreamService);

  @ViewChild('messageInput') messageInput!: ElementRef<HTMLTextAreaElement>;

  // local draft of the input box
  readonly question = signal('');

  private readonly sending = this.service.sending;
  private readonly initializing = this.service.initializing;
  private readonly isLoading = this.service.isLoading

  readonly isInputDisabled = computed(
    () => this.initializing() || this.sending() || this.isLoading(),
  );
  readonly inputPlaceholder = computed(() =>
    this.initializing() ? 'Initializing chat...' : 'Enter your message...',
  );

  // --- shell-style message history recall (up/down) ---
  // null = not navigating (editing the live draft). Otherwise an index into history.
  private historyIndex: number | null = null;
  // The draft that was in the box before navigation began, restored on stepping past newest.
  private liveDraft = '';

  constructor() {
    // Auto-focus the input once a response finishes (sending: true -> false).
    let wasSending = false;
    effect(() => {
      const sending = this.sending();
      if (wasSending && !sending) {
        setTimeout(() => this.messageInput?.nativeElement?.focus());
      }
      wasSending = sending;
    });
  }

  async prompt_agent() {
    const question = this.question().trim();
    if (!question || this.isInputDisabled()) return;
    this.question.set('');
    this.historyIndex = null;
    this.liveDraft = '';
    this.resetTextareaHeight();
    await this.service.sendMessage(question);
  }

  handleEnterKey(event: Event) {
    if ((event as KeyboardEvent).shiftKey) return;
    event.preventDefault();
    this.prompt_agent();
  }

  /**
   * Up/Down recall previously sent messages, shell-style. Only hijacks the arrow
   * when the caret is on the first line (Up) / last line (Down), so multi-line
   * editing still works normally.
   */
  navigateHistory(event: Event, direction: -1 | 1) {
    const history = this.service.history;
    if (!history.length) return;

    const textarea = this.messageInput?.nativeElement;
    if (!textarea) return;
    const value = textarea.value;
    const pos = textarea.selectionStart ?? value.length;
    const onFirstLine = !value.slice(0, pos).includes('\n');
    const onLastLine = !value.slice(pos).includes('\n');
    if (direction === -1 && !onFirstLine) return;
    if (direction === 1 && !onLastLine) return;

    if (direction === -1) {
      // Up — older.
      if (this.historyIndex === null) {
        this.liveDraft = this.question();
        this.historyIndex = history.length - 1;
      } else if (this.historyIndex > 0) {
        this.historyIndex--;
      } else {
        event.preventDefault(); // already at the oldest entry
        return;
      }
      event.preventDefault();
      this.applyRecalled(history[this.historyIndex]);
    } else {
      // Down — newer.
      if (this.historyIndex === null) return; // not navigating; leave caret movement alone
      event.preventDefault();
      if (this.historyIndex < history.length - 1) {
        this.historyIndex++;
        this.applyRecalled(history[this.historyIndex]);
      } else {
        // Stepped past the newest entry — restore the in-progress draft.
        this.historyIndex = null;
        this.applyRecalled(this.liveDraft);
      }
    }
  }

  /** Set the input to recalled text, resize, and park the caret at the end. */
  private applyRecalled(text: string) {
    this.question.set(text);
    setTimeout(() => {
      const textarea = this.messageInput?.nativeElement;
      if (!textarea) return;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
      const end = text.length;
      textarea.setSelectionRange(end, end);
    });
  }

  autoResizeTextarea(event: Event) {
    // Real typing detaches the box from history navigation.
    this.historyIndex = null;
    const textarea = event.target as HTMLTextAreaElement;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  }

  private resetTextareaHeight() {
    if (this.messageInput?.nativeElement) {
      this.messageInput.nativeElement.style.height = 'auto';
    }
  }
}
